import { LayerIndex, SidePanelLayerType } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import PDFRasterLayer from "features/map/layers/bitmap/pdf-raster/PDFRasterLayer.class"
import { PDFRasterTileLayer } from "features/map/layers/bitmap/pdf-raster/PDFRasterTileLayer"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useEffect, useState } from "react"
import { MapLayerImage } from "features/map/layers/bitmap/types"
import {
    pdfRasterImageData,
    updatePDFRasterVisibility,
} from "features/entity-caching/pdf-rasters/thunks"
import {
    selectAllRasters,
    selectRasterByIdWithLocalContext,
    selectVisibleRasters,
} from "features/entity-caching/pdf-rasters/selectors"
import { selectLocalRasterById, selectLocalRastersMap } from "./PDFRasterSlice"
import { getAncestors } from "panels/content/side-panel/utils"
import { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import { PDFRaster } from "@nova/protobuf"
import { zoomToPdfRaster } from "features/map/inspector-panel/pdf-rasters/module"
import * as Icons from "@nova/component-icons"
import {
    getGenericLayerListItem,
    getGenericLayerNameComponent,
    SidePanelListItemAction,
} from "panels/content/side-panel/generics/GenericLayerListItem"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { getGenericInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import {
    getGenericInspectorPanelHeader,
    InspectorPanelAction,
} from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { InspectorPanelPDFRasterData } from "features/map/inspector-panel/pdf-rasters/InspectorPanelPDFRasterData"
import { getGenericInspectorPanelFooter } from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import { deletePDFRaster, updatePDFRaster } from "features/pubsub/entityActions/cubes/pdfRaster"

export function registerPDFRasterPlugin(registry: PluginRegistry) {
    registry
        .register<PDFRaster>({
            name: "pdfRaster",
            displayName: "Geo-Referenced PDF",
            sidePanelLayerType: SidePanelLayerType.PDFRaster,
            PrimaryIcon: Icons.Map,
            updateAction: updatePDFRaster,
            deleteAction: deletePDFRaster,
            confirmDelete: () => true,
            protobufMessage: PDFRaster,
            selectAllEntities: selectAllRasters,
            selectSingleEntity: selectRasterByIdWithLocalContext,
            zoomTo: zoomToPdfRaster,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getPdfRasterSidePanelLayerListItem(spec),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericInspectorPanelComponent({
                spec,
                Header: getGenericInspectorPanelHeader(spec),
                Body: InspectorPanelPDFRasterData,
                Footer: getGenericInspectorPanelFooter(),
            }),
            useSubtitles: (id) => {
                const { isDownloading } = useAppSelector(selectLocalRasterById(id))
                const loadingText = useLoadingText(isDownloading)
                if (isDownloading) {
                    return <small className="text-white">{loadingText}</small>
                }
                return []
            },
            useHeaderActions: (id) => {
                const dispatch = useAppDispatch()
                const entity = useAppSelector(spec.selectSingleEntity(id))
                const { isDownloading, visible } = useAppSelector(selectLocalRasterById(id))
                const actions: InspectorPanelAction[] = []
                actions.push({
                    icon: Icons.ZoomTo,
                    onClick: () => spec.zoomTo(entity),
                    tooltipText: "Zoom to Location",
                })
                if (isDownloading) {
                    actions.push({
                        icon: Icons.Dot,
                        onClick: () => {},
                        tooltipText: undefined,
                    })
                } else {
                    actions.push({
                        icon: visible ? Icons.VisibilityEye : Icons.VisibilityEyeOff,
                        onClick: () => {
                            dispatch(updatePDFRasterVisibility({ id, visible: !visible }))
                        },
                        tooltipText: visible ? "Hide" : "Show",
                    })
                }
                return actions
            },
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => {
                const orgId = useAppSelector(selectActiveWorkspaceId())
                const projectId = useAppSelector(selectActivatedFireId)
                const localRastersById = useAppSelector(selectLocalRastersMap())
                const rasters = useAppSelector(selectVisibleRasters)
                const [rasterImages, setRasterImages] = useState<MapLayerImage[]>([])

                useEffect(() => {
                    const createRasterImagesFromUrls = async () => {
                        const newRasterImages: MapLayerImage[] = []

                        for (const data of rasters.filter((raster) => !raster.tiled)) {
                            const topLeft = data.corners[0]
                            const bottomLeft = data.corners[1]
                            const bottomRight = data.corners[2]
                            const topRight = data.corners[3]
                            const deckGLCorners: [number, number][] = [
                                [bottomLeft.longitude, bottomLeft.latitude],
                                [topLeft.longitude, topLeft.latitude],
                                [topRight.longitude, topRight.latitude],
                                [bottomRight.longitude, bottomRight.latitude],
                            ]

                            const raster: MapLayerImage = {
                                flightId: data.id,
                                bbox: deckGLCorners,
                                isVisible:
                                    !!localRastersById[data.id]?.visible &&
                                    !getAncestors(data.id, localRastersById).find(
                                        (ancestor) => !ancestor.visible
                                    ),
                            }

                            if (
                                localRastersById[data.id]?.url &&
                                localRastersById[data.id]?.url?.startsWith("blob:")
                            )
                                raster.imageSrc = localRastersById[data.id]?.url
                            else raster.imageData = pdfRasterImageData[data.id]

                            newRasterImages.push(raster)
                        }

                        setRasterImages(newRasterImages)
                    }

                    createRasterImagesFromUrls()
                }, [localRastersById, rasters])

                return [
                    new PDFRasterLayer({
                        id: "PDF-Raster-Overlap",
                        data: rasterImages,
                    }),
                    ...(orgId && projectId
                        ? rasters
                              .filter((raster) => raster.tiled)
                              .map((raster) =>
                                  PDFRasterTileLayer({
                                      id: raster.id,
                                      orgId,
                                      projectId,
                                      filename: raster.filename,
                                      corners: raster.corners,
                                  })
                              )
                        : []),
                ]
            },
            zIndex: LayerIndex.PDFRaster,
        }))
}

function getPdfRasterSidePanelLayerListItem(spec: PluginSpec<PDFRaster>) {
    const GenericSidePanelListItem = getGenericLayerListItem({
        spec,
        useActions: (id) => {
            const dispatch = useAppDispatch()
            const entity = useAppSelector(spec.selectSingleEntity(id))
            const { visible, isDownloading } = useAppSelector(selectLocalRasterById(id))
            const actions = [] as SidePanelListItemAction[]
            if (isDownloading) return actions
            actions.push({
                icon: Icons.ZoomTo,
                onClick: () => {
                    zoomToPdfRaster(entity)
                },
            })
            actions.push({
                icon: visible ? Icons.VisibilityEye : Icons.VisibilityEyeOff,
                onClick: () => {
                    dispatch(updatePDFRasterVisibility({ id, visible: !visible }))
                },
            })
            return actions
        },
        LayerNameComponent: getPdfRasterLayerNameComponent(spec),
    })
    const SidePanelListItem = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        return <GenericSidePanelListItem id={id} isHovered={isHovered} />
    }
    return SidePanelListItem
}

function getPdfRasterLayerNameComponent(spec: PluginSpec<PDFRaster>) {
    const GenericLayerNameComponent = getGenericLayerNameComponent(spec)
    const LayerNameComponent = ({ id }: { id: string }) => {
        const { isDownloading } = useAppSelector(selectLocalRasterById(id))
        const loadingText = useLoadingText(isDownloading)
        if (isDownloading) {
            return <small>{loadingText}</small>
        }
        return <GenericLayerNameComponent id={id} />
    }
    return LayerNameComponent
}
